import { useEffect, useMemo, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AREAS,
  CATEGORIES,
  STATUSES,
  PRIORITIES,
  DEPARTMENTS,
  STATUS_STYLES,
  PRIORITY_STYLES,
  composeLocation,
} from "@/lib/constants";
import { formatAge, isUrgentOpen } from "@/lib/ticketUtils";
import { downloadCsv } from "@/lib/csv";
import TicketDetailDialog from "@/components/TicketDetailDialog";
import { Search, X, Filter, Loader2, Download, Flame } from "lucide-react";

const ALL = "__all__";

function fmtDate(s) {
  if (!s) return "—";
  const d = new Date(s);
  return d.toLocaleString(undefined, {
    year: "numeric", month: "short", day: "2-digit",
    hour: "2-digit", minute: "2-digit",
  });
}

function fmtCsvDate(s) {
  if (!s) return "";
  // ISO with timezone keeps Excel from auto-converting incorrectly
  return new Date(s).toISOString();
}

export default function AdminTickets() {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const [filters, setFilters] = useState({
    area: ALL, category: ALL, status: ALL, priority: ALL, department: ALL,
    date_from: "", date_to: "", search: "",
  });

  // Open ticket from ?open=<id> (e.g. push notification click)
  useEffect(() => {
    const openId = searchParams.get("open");
    if (openId) {
      setSelectedId(openId);
      searchParams.delete("open");
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const buildParams = useCallback(() => {
    const p = {};
    Object.entries(filters).forEach(([k, v]) => {
      if (v && v !== ALL) p[k] = v;
    });
    return p;
  }, [filters]);

  const fetchTickets = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/tickets", { params: buildParams() });
      setTickets(data);
    } finally {
      setLoading(false);
    }
  }, [buildParams]);

  useEffect(() => {
    fetchTickets();
  }, [fetchTickets]);

  const clearFilters = () => {
    setFilters({
      area: ALL, category: ALL, status: ALL, priority: ALL, department: ALL,
      date_from: "", date_to: "", search: "",
    });
  };

  const activeCount = useMemo(
    () => Object.entries(filters).filter(([, v]) => v && v !== ALL).length,
    [filters]
  );

  const exportCsv = () => {
    const spec = [
      { key: "ticket_no", header: "Ticket No" },
      { key: "area", header: "Area" },
      { key: "category", header: "Category" },
      { key: "name", header: "Name / Username" },
      { key: "location", header: "Location / Block / Room", get: (r) => composeLocation(r) },
      { key: "priority", header: "Priority" },
      { key: "status", header: "Status" },
      { key: "department", header: "Department" },
      { key: "description", header: "Description" },
      { key: "admin_note", header: "Admin Note" },
      { key: "created_at", header: "Created Date", get: (r) => fmtCsvDate(r.created_at) },
      { key: "updated_at", header: "Last Updated Date", get: (r) => fmtCsvDate(r.updated_at) },
    ];
    const stamp = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 19);
    downloadCsv(`agup2-tickets-${stamp}.csv`, spec, tickets);
  };

  return (
    <div className="p-6 lg:p-10 max-w-[1600px] mx-auto" data-testid="admin-tickets-page">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-5">
        <div>
          <div className="text-[11px] uppercase tracking-[0.18em] text-[color:var(--enka-muted)] font-semibold">
            IQ3B9
          </div>
          <h1 className="font-display text-3xl text-[color:var(--enka-ink)] mt-1">Tickets</h1>
          <p className="text-sm text-[color:var(--enka-muted)]">
            {tickets.length} ticket{tickets.length === 1 ? "" : "s"}
            {activeCount > 0 ? ` · ${activeCount} filter${activeCount === 1 ? "" : "s"} active` : ""}
          </p>
        </div>
        <Button
          type="button"
          variant="outline"
          onClick={exportCsv}
          disabled={loading || tickets.length === 0}
          data-testid="tickets-export-csv-btn"
          className="border-[color:var(--enka-blue)] text-[color:var(--enka-blue)] hover:bg-[color:var(--enka-blue-50)]"
        >
          <Download className="h-4 w-4 mr-2" />
          Export CSV ({tickets.length})
        </Button>
      </div>

      <Card className="p-4 mb-4" data-testid="filters-card">
        <div className="flex items-center gap-2 mb-3">
          <Filter className="h-4 w-4 text-[color:var(--enka-blue)]" />
          <span className="text-sm font-medium text-[color:var(--enka-ink)]">Filters</span>
          {activeCount > 0 && (
            <button
              type="button"
              onClick={clearFilters}
              className="ml-auto text-xs text-[color:var(--enka-muted)] hover:text-[color:var(--enka-blue)] flex items-center gap-1"
              data-testid="filters-clear-btn"
            >
              <X className="h-3 w-3" /> Clear all
            </button>
          )}
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          <div>
            <Label className="text-xs">Area</Label>
            <Select value={filters.area} onValueChange={(v) => setFilters((s) => ({ ...s, area: v }))}>
              <SelectTrigger data-testid="filter-area"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>All</SelectItem>
                {AREAS.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Category</Label>
            <Select value={filters.category} onValueChange={(v) => setFilters((s) => ({ ...s, category: v }))}>
              <SelectTrigger data-testid="filter-category"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>All</SelectItem>
                {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Status</Label>
            <Select value={filters.status} onValueChange={(v) => setFilters((s) => ({ ...s, status: v }))}>
              <SelectTrigger data-testid="filter-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>All</SelectItem>
                {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Priority</Label>
            <Select value={filters.priority} onValueChange={(v) => setFilters((s) => ({ ...s, priority: v }))}>
              <SelectTrigger data-testid="filter-priority"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>All</SelectItem>
                {PRIORITIES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Department</Label>
            <Select value={filters.department} onValueChange={(v) => setFilters((s) => ({ ...s, department: v }))}>
              <SelectTrigger data-testid="filter-department"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>All</SelectItem>
                {DEPARTMENTS.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Search</Label>
            <div className="relative">
              <Search className="h-4 w-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-[color:var(--enka-muted)]" />
              <Input
                value={filters.search}
                onChange={(e) => setFilters((s) => ({ ...s, search: e.target.value }))}
                placeholder="Ticket no, name, location..."
                className="pl-8"
                data-testid="filter-search"
              />
            </div>
          </div>
          <div>
            <Label className="text-xs">From date</Label>
            <Input
              type="date"
              value={filters.date_from}
              onChange={(e) => setFilters((s) => ({ ...s, date_from: e.target.value }))}
              data-testid="filter-date-from"
            />
          </div>
          <div>
            <Label className="text-xs">To date</Label>
            <Input
              type="date"
              value={filters.date_to}
              onChange={(e) => setFilters((s) => ({ ...s, date_to: e.target.value }))}
              data-testid="filter-date-to"
            />
          </div>
          <div className="flex items-end">
            <Button
              type="button"
              onClick={fetchTickets}
              className="w-full bg-[color:var(--enka-blue)] hover:bg-[color:var(--enka-blue-600)] text-white"
              data-testid="filters-apply-btn"
            >
              Apply
            </Button>
          </div>
        </div>
      </Card>

      <Card className="overflow-hidden" data-testid="tickets-table-card">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="h-6 w-6 animate-spin text-[color:var(--enka-blue)]" />
          </div>
        ) : tickets.length === 0 ? (
          <div className="py-16 text-center text-[color:var(--enka-muted)]" data-testid="tickets-empty">
            No tickets match the current filters.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-[color:var(--enka-blue-50)]/60 hover:bg-[color:var(--enka-blue-50)]/60">
                  <TableHead className="font-semibold text-[color:var(--enka-ink)]">Ticket No</TableHead>
                  <TableHead className="font-semibold text-[color:var(--enka-ink)]">Area</TableHead>
                  <TableHead className="font-semibold text-[color:var(--enka-ink)]">Category</TableHead>
                  <TableHead className="font-semibold text-[color:var(--enka-ink)]">Location</TableHead>
                  <TableHead className="font-semibold text-[color:var(--enka-ink)]">Priority</TableHead>
                  <TableHead className="font-semibold text-[color:var(--enka-ink)]">Status</TableHead>
                  <TableHead className="font-semibold text-[color:var(--enka-ink)]">Department</TableHead>
                  <TableHead className="font-semibold text-[color:var(--enka-ink)]">Age</TableHead>
                  <TableHead className="font-semibold text-[color:var(--enka-ink)]">Created</TableHead>
                  <TableHead className="font-semibold text-[color:var(--enka-ink)]">Updated</TableHead>
                  <TableHead className="text-right font-semibold text-[color:var(--enka-ink)]">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {tickets.map((t) => {
                  const urgent = isUrgentOpen(t);
                  return (
                    <TableRow
                      key={t.id}
                      className={`cursor-pointer transition-colors ${
                        urgent
                          ? "bg-red-50/40 hover:bg-red-50/70 border-l-4 border-l-red-500"
                          : "hover:bg-[color:var(--enka-blue-50)]/40"
                      }`}
                      onClick={() => setSelectedId(t.id)}
                      data-testid={`ticket-row-${t.ticket_no}`}
                      data-urgent-open={urgent ? "true" : "false"}
                    >
                      <TableCell className="font-mono font-semibold text-[color:var(--enka-blue)]">
                        <div className="flex items-center gap-1.5">
                          {urgent && <Flame className="h-3.5 w-3.5 text-red-600" />}
                          {t.ticket_no}
                        </div>
                      </TableCell>
                      <TableCell>{t.area}</TableCell>
                      <TableCell>{t.category}</TableCell>
                      <TableCell className="max-w-[260px] truncate" title={composeLocation(t)}>
                        {composeLocation(t)}
                      </TableCell>
                      <TableCell>
                        <span className={`enka-pill ${PRIORITY_STYLES[t.priority]}`}>{t.priority}</span>
                      </TableCell>
                      <TableCell>
                        <span className={`enka-pill ${STATUS_STYLES[t.status]}`}>{t.status}</span>
                      </TableCell>
                      <TableCell className="text-sm">{t.department}</TableCell>
                      <TableCell
                        className={`text-sm whitespace-nowrap font-medium ${
                          urgent ? "text-red-700" : "text-[color:var(--enka-muted)]"
                        }`}
                        data-testid={`ticket-age-${t.ticket_no}`}
                      >
                        {formatAge(t.created_at)}
                      </TableCell>
                      <TableCell className="text-sm text-[color:var(--enka-muted)] whitespace-nowrap">
                        {fmtDate(t.created_at)}
                      </TableCell>
                      <TableCell className="text-sm text-[color:var(--enka-muted)] whitespace-nowrap">
                        {fmtDate(t.updated_at)}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          onClick={(e) => { e.stopPropagation(); setSelectedId(t.id); }}
                          data-testid={`ticket-open-btn-${t.ticket_no}`}
                        >
                          Open
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}
      </Card>

      <TicketDetailDialog
        ticketId={selectedId}
        onClose={() => setSelectedId(null)}
        onUpdated={(updated) => {
          setTickets((list) => list.map((t) => (t.id === updated.id ? updated : t)));
        }}
      />
    </div>
  );
}
